import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { WalletResponse, WalletTradesResponse } from "../api/types";

/**
 * Профиль кошелька-контрагента и наши встречи с ним.
 *
 * TanStack Query, а не zustand-стор: сливать сокетный поток с REST-гидрацией
 * здесь нечего — та же граница, что проведена в usePositions. Живой поток по
 * тирам всё-таки есть (md.profiles), но он питает useTiers и нужен ЛЕНТЕ; на
 * открытой странице кошелька достаточно опроса.
 */
export function walletKey(address: string | undefined) {
  return ["wallet", address ?? ""] as const;
}

export function walletTradesKey(address: string | undefined) {
  return ["wallet-trades", address ?? ""] as const;
}

/**
 * Как часто перечитывать профиль.
 *
 * Заметно реже позиций (3 с): метрики пересчитываются трейдером раз в
 * PROFILE_REFRESH (десять минут по умолчанию), и опрос чаще возвращал бы то же
 * самое. Полминуты выбраны так, чтобы страница, открытая в момент первой
 * встречи с кошельком, увидела приехавший бэкфилл сама, без F5.
 */
const POLL_MS = 30_000;

/**
 * Опрашиваем и в НЕВИДИМОЙ вкладке.
 *
 * Обе ручки читают НАШ Postgres — профиль кошелька и наши же сделки с ним
 * (webapi.handleWallet → s.wallets.Profile), — то есть полминутный опрос стоит
 * одного SELECT и к чужому лимитеру не ходит вовсе.
 *
 * А «невидимая вкладка» не означает «никто не смотрит»: встроенный предпросмотр
 * IDE всегда сообщает hidden, и страница кошелька стояла замороженной, пока на
 * неё смотрели. Тот же разбор и тот же выбор, что в useParticipants.
 */
const IN_BACKGROUND = true;

export function useWallet(address: string | undefined) {
  return useQuery<WalletResponse>({
    queryKey: walletKey(address),
    queryFn: () => api.wallet(address ?? ""),
    enabled: Boolean(address),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: IN_BACKGROUND,
    // 404 — это ОТВЕТ («мы с этим кошельком не торговали»), а не сбой связи, и
    // повторять запрос смысла нет: результат не изменится до следующей сделки.
    retry: (count, error) =>
      count < 1 && !(error instanceof Error && error.message.includes("не встречался")),
  });
}

export function useWalletTrades(address: string | undefined) {
  return useQuery<WalletTradesResponse>({
    queryKey: walletTradesKey(address),
    queryFn: () => api.walletTrades(address ?? ""),
    enabled: Boolean(address),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: IN_BACKGROUND,
  });
}
